// src/lib/auth.js
// HomeIQa Agent CRM — auth helpers, admin gate, agent provisioning
import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp, collection, addDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

// ── Admin email(s). Add more here to grant admin powers without re-deploying logic.
export const ADMIN_EMAILS = [
  'paperwriter20@gmail.com',
  'tlaketiajones@gmail.com'
];

export const isAdminEmail = (email) =>
  !!email && ADMIN_EMAILS.map(e => e.toLowerCase()).includes(email.toLowerCase());

// Provision the agent doc on first login:
//   - admin email → role: 'admin', status: 'active'
//   - has matching agentInvites/{email} doc → role: 'agent', status: 'active' (and delete the invite)
//   - otherwise → throw 'UNAUTHORIZED_EMAIL' so AgentLogin can sign them out.
//
// Strict mode: no pending records are created for unknown emails. Strangers
// are immediately rejected at the door.
export async function provisionAgent(user) {
  if (!user) return null;
  const ref = doc(db, 'agents', user.uid);
  const snap = await getDoc(ref);

  // Admin always wins.
  const adminUser = isAdminEmail(user.email);

  if (snap.exists()) {
    const data = snap.data();
    // Keep admin role in sync if email is now whitelisted.
    if (adminUser && data.role !== 'admin') {
      await setDoc(ref, { role: 'admin', status: 'active' }, { merge: true });
      data.role = 'admin'; data.status = 'active';
    }
    // Existing pending records (from before strict mode) get rejected too —
    // the only way in is to be admin or have an active record from an invite.
    if (data.status !== 'active' && !adminUser) {
      throw new Error('UNAUTHORIZED_EMAIL');
    }
    // Stamp the login event into the agent's audit log + bump lastLoginAt.
    await logLoginEvent(user.uid, user.email);
    return data;
  }

  // Bootstrap from invite if one exists for this email.
  let role = 'agent'; // 'admin' | 'agent' | 'lead_submitter'
  let status = 'active';
  let invitedBy = null;
  let invitedByUid = null;

  if (adminUser) {
    role = 'admin';
  } else {
    const inviteRef = doc(db, 'agentInvites', user.email.toLowerCase());
    const inviteSnap = await getDoc(inviteRef);
    if (inviteSnap.exists()) {
      const inv = inviteSnap.data();
      invitedBy = inv.invitedBy || null;
      invitedByUid = inv.invitedByUid || null;
      // Role from invite: 'agent' (default) or 'lead_submitter'
      if (inv.role === 'lead_submitter') role = 'lead_submitter';
      else role = 'agent';
      await deleteDoc(inviteRef).catch(() => {});
    } else {
      // No admin status, no invite → reject. Do NOT create a pending record.
      throw new Error('UNAUTHORIZED_EMAIL');
    }
  }

  // Auto-derived mailbox alias on the verified Mailgun domain. The agent
  // can override this in Settings if they prefer a different format.
  // Lead submitters don't get a mailbox.
  const mailboxAddress = role === 'lead_submitter'
    ? null
    : deriveMailboxAddress(user.email, user.displayName);

  // Per-agent public lead-intake slug. Anyone with the link can submit
  // a property — the submission is auto-credited to this agent. Format:
  // "first-lastInitial-{4-char random}" so it's both readable and unique.
  const intakeSlug = deriveIntakeSlug(user.email, user.displayName);

  const profile = {
    uid: user.uid,
    email: user.email,
    name: user.displayName || '',
    photoURL: user.photoURL || '',
    role,
    status,
    invitedBy,
    invitedByUid,
    // Commission split for contract agents. HomeIQa keeps 25% by default;
    // the agent gets the remaining 75% of every successful deal. Per-lead
    // overrides live on the lead doc as commissionOverrideToHomeIQa.
    // Admin / lead_submitter ignore this.
    defaultCommissionToHomeIQa: role === 'agent' ? 0.25 : null,
    mailboxAddress,
    intakeSlug,
    introSms: {
      // false = ask before sending each lead (recommended while testing)
      // true  = auto-fire on every new lead
      enabled: false,
      template: defaultIntroSmsForRole(role)
    },
    createdAt: serverTimestamp(),
    lastLoginAt: serverTimestamp()
  };
  await setDoc(ref, profile, { merge: true });
  await logLoginEvent(user.uid, user.email, { firstLogin: true });
  return profile;
}

// Append a login event to the agent's audit log subcollection. Each entry
// records the timestamp + a soft user-agent fingerprint so the owner can
// see "Denicia logged in from Mac/Chrome at 9:14 AM" if needed.
async function logLoginEvent(uid, email, extra = {}) {
  try {
    const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '').slice(0, 240) : '';
    const evt = {
      event: 'login',
      at: Timestamp.now(),
      email: email || null,
      userAgent: ua,
      ...extra
    };
    // Update lastLoginAt on the profile + record the event in auditLog.
    await Promise.all([
      setDoc(doc(db, 'agents', uid), { lastLoginAt: Timestamp.now() }, { merge: true }),
      addDoc(collection(db, 'agents', uid, 'auditLog'), evt)
    ]);
  } catch (e) {
    console.warn('logLoginEvent failed:', e?.message || e);
  }
}

// Default intro SMS the contract agent / owner sends to a fresh lead.
// Contract agents work on behalf of HomeIQa (they're not the buyer
// themselves) so the wording is "I have someone interested in buying it"
// rather than "I'm interested in buying it." Admin/owner default uses
// the same agent-facing phrasing — Laketia's name fills in via the
// {agentName} interpolation.
export function defaultIntroSmsForRole(role) {
  if (role === 'lead_submitter') {
    return ''; // lead submitters don't send SMS, no template needed
  }
  if (role === 'agent') {
    return 'Hello, my name is {agentName}. I saw that you have a house for sale in {city}, {state} and I have someone interested in buying it, do you mind if I call to talk about it?';
  }
  // admin / owner
  return 'Hello, my name is {agentName}. I saw that you have a house for sale in {city}, {state} and I have someone interested in buying it, do you mind if I call to talk about it?';
}

// Generate a human-readable lead-intake slug (e.g. "denicia-w-7f3a")
// for an agent. Used as the path on /lead-intake/:slug so anyone with
// the link can submit a property and have it auto-attributed.
export function deriveIntakeSlug(email, displayName) {
  const base = (() => {
    const name = (displayName || '').trim();
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0]}-${parts[parts.length - 1].charAt(0)}`.toLowerCase().replace(/[^a-z0-9-]/g, '');
      }
      return parts[0].toLowerCase().replace(/[^a-z0-9]/g, '');
    }
    return (email || '').split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
  })();
  // Short random suffix so slugs are unique across name collisions and
  // can't be guessed.
  const rand = Math.random().toString(36).slice(2, 6);
  return `${base || 'agent'}-${rand}`;
}

// Build a sensible default mailbox alias for a new contract agent.
// Tries first.last from the display name, falls back to the email's
// local part. Sits on the root homeiqa.com domain (verified in Mailgun
// as of 2026-05-09) — both sending and receiving go through Mailgun's
// catch-all route on *@homeiqa.com.
export function deriveMailboxAddress(email, displayName) {
  const base = (() => {
    const name = (displayName || '').trim();
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        const first = parts[0].toLowerCase();
        const last = parts[parts.length - 1].toLowerCase();
        return `${first}.${last.charAt(0)}`.replace(/[^a-z0-9.]/g, '');
      }
      return parts[0].toLowerCase().replace(/[^a-z0-9]/g, '');
    }
    return (email || '').split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
  })();
  return `${base}@homeiqa.com`;
}

// Hook: returns { user, profile, loading }. profile is the agents/{uid} doc.
export function useAgentAuth() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const p = await provisionAgent(u);
          setProfile(p);
        } catch (e) {
          console.error('provisionAgent failed', e);
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  return { user, profile, loading };
}
